import { Divider, Dropdown, message, Popover, Row } from "antd";
import UserCard from "../UserCard";
import { useModel } from "@umijs/max";
import Notification from "../Notification";
import { eventBus } from "@/utils/eventBus";
import { events, ResponseCode } from "@/constants";
import {
  getMessagesService,
  MessageStatus,
  readMessageService,
} from "@/services/message";
import { useEffect, useState } from "react";
import { debounceFn } from "@/utils";
import { useRequest } from "ahooks";
import styles from "./index.less";
import { ReactComponent as MenuExpandIcon } from "@/assets/images/menu-expand.svg";
import { ReactComponent as BellIcon } from "@/assets/images/bell.svg";
import { ReactComponent as BellDotIcon } from "@/assets/images/bell-dot.svg";
import { PricingPlanVersionEnum } from "@packages/pricing-components";
import PlanTag from "@packages/pricing-components/src/components/PlanTag";
import Avatar from "../Avatar";

type Props = {
  showHeader: boolean;
  collapsed: boolean;
  onCollapsed: (collapsed: boolean) => void;
};
export default function UserHeader(props: Props) {
  const { showHeader = true, collapsed, onCollapsed } = props;
  const { initialState, refresh: refreshInitialState } =
    useModel("@@initialState");
  const userPlanSubscriptionInformation =
    initialState?.userPlanSubscriptionInformation;

  const [openUserCard, setOpenUserCard] = useState<boolean>(false);

  const {
    data: msgs,
    run: reloadMsgs,
    loading: msgLoading,
  } = useRequest(async () => {
    const rsp = await getMessagesService({
      current: 1,
      size: 20,
      userId: initialState?.userInfo?.user.userId,
      status: MessageStatus.Unread,
    });
    return rsp.data?.records;
  });

  useEffect(() => {
    const cb = debounceFn(() => {
      reloadMsgs();
    }, 200);
    eventBus.subscribe(events.notification, cb);

    return () => {
      eventBus.destroy(events.notification, cb);
    };
  }, []);

  // 点击页面其他区域，关闭用户卡片
  useEffect(() => {
    const handleClick = () => {
      setOpenUserCard(false);
    };
    if (openUserCard) {
      document.addEventListener("click", handleClick);
    }
    return () => {
      if (openUserCard) {
        document.removeEventListener("click", handleClick);
      }
    };
  }, [openUserCard]);

  const bellCls = !collapsed ? `mr-[18px]` : "mt-[21px] w-full";

  return (
    <div className="w-full">
      <Row
        justify={!collapsed ? "space-between" : "center"}
        align={"middle"}
        className="w-full relative"
      >
        <Dropdown
          open={openUserCard}
          dropdownRender={() => {
            return <UserCard onClose={() => setOpenUserCard(false)} />;
          }}
          trigger={["click"]}
          onOpenChange={(open) => {
            if (open) {
              refreshInitialState();
            }
          }}
        >
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setOpenUserCard(!openUserCard);
            }}
          >
            <div className="flex items-center justify-center ">
              <Avatar
                images={initialState?.userInfo?.user.avatar}
                fallback={
                  initialState?.userInfo?.user?.nickName ||
                  initialState?.userInfo?.user?.userName
                }
                className={styles.avatar}
              />

              {(showHeader || !collapsed) && (
                <div className="flex flex-col justify-start items-start gap-[3px] ml-[12px]">
                  {showHeader && (
                    <div>
                      <Row wrap={false}>
                        <div className={styles.username}>
                          {initialState?.userInfo?.user?.nickName ||
                            initialState?.userInfo?.user?.userName}
                        </div>
                        <div className={styles["dropdown-arrow"]} />
                      </Row>
                    </div>
                  )}

                  {!collapsed && (
                    <PlanTag
                      version={
                        userPlanSubscriptionInformation?.paidVersion ??
                        PricingPlanVersionEnum.BASIC
                      }
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Dropdown>

        <Popover
          trigger="click"
          content={
            <Notification
              loading={msgLoading}
              data={msgs}
              onRead={async (value) => {
                const rsp = await readMessageService(
                  {
                    userId: initialState?.userInfo?.user?.userId,
                  },
                  value?.id
                );
                if (rsp.code === ResponseCode.Success) {
                  eventBus.emit(events.notification);
                  message.success("操作成功");
                  return true;
                } else {
                  message.error(rsp.msg);
                  return false;
                }
              }}
            />
          }
          arrow={false}
          placement="bottomLeft"
        >
          {msgs?.length ? (
            <BellDotIcon className={`${styles.bell} ${bellCls}`} />
          ) : (
            <BellIcon className={`${styles.bell} ${bellCls}`} />
          )}
        </Popover>

        <div
          className={styles["expand-icon"]}
          onClick={() => onCollapsed(!collapsed)}
        >
          <MenuExpandIcon
            style={{
              transform: !collapsed ? "rotateY(0deg)" : "rotateY(180deg)",
            }}
          />
        </div>
      </Row>
      {!showHeader && (
        <div className="px-1">
          <Divider className="my-6" />
        </div>
      )}
    </div>
  );
}
